<template>
  <form class="prompt-composer" @submit.prevent="$emit('submit')">
    <div v-if="uploads.length" class="prompt-composer__uploads">
      <div v-for="item in uploads" :key="item.id" class="prompt-upload">
        <CustomImg :src="item.url" :alt="item.file.name" />
        <button type="button" aria-label="Remove image" @click="$emit('remove-upload', item.id)">×</button>
      </div>

      <label class="prompt-upload prompt-upload--add" :class="{ 'is-disabled': uploadDisabled }">
        <input type="file" accept="image/*" multiple :disabled="uploadDisabled" @change="selectFiles" />
        <span>＋</span>
      </label>
    </div>
    <p v-if="uploads.length > 1" class="prompt-composer__upload-note">
      {{ t('ai.prompt.firstImageOnly') }}
    </p>

    <!-- 输入框 -->
    <textarea
      :value="modelValue"
      :placeholder="placeholder"
      rows="1"
      @input="$emit('update:modelValue', ($event.target as HTMLTextAreaElement).value)"
      @keydown="handleKeydown"
    ></textarea>

    <div class="prompt-composer__actions">
      <div ref="skillSelectorRef" class="skill-selector">
        <button class="skill-selector__trigger" type="button" :disabled="skillDisabled" @click="open = !open">
          <CustomImg :src="skillIcon" alt="" />
          <span>{{ t(activeSkill.titleKey) }}</span>
          <CustomImg :src="arrowIcon" alt="" />
        </button>

        <div v-if="open" class="skill-selector__menu">
          <button
            v-for="skill in skills"
            :key="skill.key"
            type="button"
            :class="{ 'is-active': skill.key === activeSkill.key }"
            @click="chooseSkill(skill.key)"
          >
            <CustomImg :src="menuIcon" alt="" />
            <span>
              <strong>{{ t(skill.titleKey) }}</strong>
              <small>{{ t(skill.descriptionKey) }}</small>
            </span>
          </button>
        </div>
      </div>

      <div class="prompt-composer__buttons">
        <label v-if="!uploads.length" class="prompt-composer__upload-button" :class="{ 'is-disabled': uploadDisabled }">
          <input type="file" accept="image/*" multiple :disabled="uploadDisabled" @change="selectFiles" />
          <span>＋</span>
        </label>
        <!-- 暂时不接入语音识别 -->
        <!-- <button class="prompt-composer__icon-button" type="button">
          <CustomImg :src="voiceIcon" alt="" />
        </button> -->
        <button class="prompt-composer__icon-button prompt-composer__submit" type="submit" :disabled="disabled">
          <CustomImg :src="submitIcon" alt="" />
        </button>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import CustomImg from '@/components/CustomImg.vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import arrowIcon from '@/assets/images/ai/icon4.svg';
import menuIcon from '@/assets/images/ai/icon5.svg';
import skillIcon from '@/assets/images/ai/icon3.svg';
import submitIcon from '@/assets/images/ai/icon7.svg';
import voiceIcon from '@/assets/images/ai/icon6.svg';
import type { SkillOption, Translate, UploadItem } from './types';

const props = defineProps<{
  t: Translate;
  modelValue: string;
  placeholder: string;
  uploads: UploadItem[];
  skills: SkillOption[];
  selectedSkill: string;
  disabled?: boolean;
  uploadDisabled?: boolean;
  skillDisabled?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'select-files': [files: File[]];
  'remove-upload': [id: string];
  'select-skill': [key: string];
  'submit': [];
}>();
// 回车逻辑
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Enter') return

  // 输入法正在组合文字时，不提交
  if (event.isComposing) return

  // Shift/Ctrl/Alt/Meta + Enter 不处理
  if (
    event.shiftKey ||
    event.ctrlKey ||
    event.altKey ||
    event.metaKey
  ) {
    return
  }

  event.preventDefault()
  emit('submit')
}

const open = ref(false);
const skillSelectorRef = ref<HTMLElement | null>(null);
const activeSkill = computed<SkillOption>(() => (
  props.skills.find(item => item.key === props.selectedSkill) ?? props.skills[0]!
));

const chooseSkill = (key: string) => {
  emit('select-skill', key);
  open.value = false;
};

const selectFiles = (event: Event) => {
  const input = event.target as HTMLInputElement;
  emit('select-files', Array.from(input.files ?? []));
  input.value = '';
};

const closeSkillMenuOnOutsideClick = (event: PointerEvent) => {
  if (!skillSelectorRef.value?.contains(event.target as Node)) open.value = false;
};

watch(() => props.skillDisabled, disabled => {
  if (disabled) open.value = false;
});

onMounted(() => document.addEventListener('pointerdown', closeSkillMenuOnOutsideClick));
onBeforeUnmount(() => document.removeEventListener('pointerdown', closeSkillMenuOnOutsideClick));
</script>

<style scoped lang="scss">
.prompt-composer {
  position: relative;
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  width: 100%;
  min-height: 144px;
  padding: 16px 24px 12px;
  border: 1px solid rgba(88, 189, 110, .8);
  border-radius: 30px;
  background: #fff;
  box-shadow: 0 15px 30px rgba(108, 204, 142, .2);

  > textarea {
    display: block;
    width: 100%;
    height: 54px;
    padding: 0;
    border: 0;
    outline: 0;
    resize: none;
    color: #333;
    background: transparent;
    line-height: 1.5;

    &::placeholder { color: #aaa; }
  }
}

.prompt-composer__uploads {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.prompt-composer__upload-note {
  margin: -10px 0 12px;
  color: #888;
  font-size: 12px;
}

.prompt-upload {
  position: relative;
  display: block;
  width: 64px;
  height: 64px;
  border-radius: 10px;
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  > button {
    position: absolute;
    top: 2px;
    right: 2px;
    display: grid;
    width: 18px;
    height: 18px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    color: #fff;
    background: var(--green);
    place-items: center;
    cursor: pointer;
  }

  &--add {
    display: grid;
    color: var(--green);
    background: #f5f5f5;
    font-size: 30px;
    cursor: pointer;
    place-items: center;
    transition: background-color .2s ease, box-shadow .2s ease, transform .2s ease;
  }

  input { display: none; }
}

.prompt-composer__actions {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.skill-selector { position: relative; }

.skill-selector__trigger {
  display: flex;
  align-items: center;
  height: 38px;
  padding: 0 16px;
  border: 1px solid rgba(88, 189, 110, .75);
  border-radius: 999px;
  color: var(--green);
  background: rgba(88, 189, 110, .05);
  cursor: pointer;

  &:disabled {
    opacity: .55;
    cursor: not-allowed;
  }

  img:first-child {
    width: 20px;
    margin-right: 8px;
  }

  img:last-child {
    width: 18px;
    margin-left: 8px;
  }
}

.skill-selector__menu {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 0;
  z-index: 20;
  width: 310px;
  padding: 8px;
  border: 1px solid rgba(0, 0, 0, .08);
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 14px 34px rgba(0, 0, 0, .12);

  button {
    display: flex;
    width: 100%;
    padding: 10px;
    border: 0;
    border-radius: 10px;
    text-align: left;
    background: transparent;
    cursor: pointer;

    &:hover,
    &.is-active { background: #f2faf5; }

    > img {
      width: 25px;
      margin: 2px 10px 0 0;
    }

    span {
      display: flex;
      flex-direction: column;
    }

    strong { color: #333; }

    small {
      margin-top: 3px;
      color: #999;
    }
  }
}

.prompt-composer__buttons {
  display: flex;
  align-items: center;
  gap: 10px;

  input { display: none; }
}

.prompt-composer__upload-button,
.prompt-composer__icon-button {
  display: grid;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #777;
  background: #f7f7f7;
  cursor: pointer;
  place-items: center;
  transition: background-color .2s ease, box-shadow .2s ease, transform .2s ease;

  &.is-disabled {
    opacity: .55;
    cursor: not-allowed;
  }
}

@media (hover: hover) {
  .prompt-upload--add:not(.is-disabled):hover,
  .prompt-composer__upload-button:not(.is-disabled):hover {
    background: rgba(88, 189, 110, .12);
    box-shadow: 0 4px 12px rgba(88, 189, 110, .18);
    transform: translateY(-1px);
  }
}

.prompt-composer__icon-button img { width: 20px; }

.prompt-composer__submit {
  background: linear-gradient(135deg, #49c66d, #17aeb1);

  img { width: 22px; }
  &:disabled { opacity: .55; cursor: not-allowed; }
}

@media (max-width: 600px) {
  .prompt-composer {
    min-height: 126px;
    padding: 14px;
    border-radius: 22px;
  }

  .skill-selector__trigger {
    max-width: 180px;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .skill-selector__menu { width: min(290px, calc(100vw - 30px)); }
  .prompt-composer__upload-button { display: none; }
}
</style>
